import { useCallback, useEffect, useState } from 'react';
import type { LngLat } from './types.ts';
import { bearingDeg, distanceM } from './geo.ts';

const KEY = 'fairway.parked.v1';

export interface Parked {
  pos: LngLat;
  at: number;
  hole: number | null;
}

/**
 * "Where did I park the buggy."
 *
 * Two real problems this solves, both of which golfers hit constantly:
 *
 *  1. You park on the path, walk to your ball with two clubs, and want to know
 *     what the shot actually is before you decide which two clubs. Distance
 *     from the BUGGY to the green tells you that while you are still standing
 *     at the buggy.
 *  2. You then have to find the buggy again — on a blind hole, in trees, or
 *     when someone else moved it. Bearing and distance back solves it.
 *
 * Stored on the device, not the server: it is worthless five minutes later and
 * nobody else needs it.
 */
export function useParked() {
  const [parked, setParked] = useState<Parked | null>(() => {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as Parked) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (parked) localStorage.setItem(KEY, JSON.stringify(parked));
    else localStorage.removeItem(KEY);
  }, [parked]);

  const park = useCallback((pos: LngLat, hole: number | null) => {
    setParked({ pos, at: Date.now(), hole });
  }, []);

  const clear = useCallback(() => setParked(null), []);

  return { parked, park, clear };
}

export interface ParkedInfo {
  /** How far the golfer has walked from the buggy. */
  fromMe: number;
  /** Compass bearing back to the buggy. */
  bearingToBuggy: number;
  /** The number that matters at the buggy: buggy to the middle of the green. */
  buggyToGreen: number;
  /** Minutes since parking — a stale marker is usually a forgotten one. */
  ageMin: number;
}

export function parkedInfo(parked: Parked, me: LngLat | null, green: LngLat): ParkedInfo {
  return {
    fromMe: me ? distanceM(me, parked.pos) : 0,
    bearingToBuggy: me ? bearingDeg(me, parked.pos) : 0,
    buggyToGreen: distanceM(parked.pos, green),
    ageMin: Math.round((Date.now() - parked.at) / 60_000),
  };
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

export function compassPoint(bearing: number): string {
  return COMPASS[Math.round(((bearing % 360) + 360) % 360 / 45) % 8];
}

/**
 * Golfers do not read bearings. "Behind you" is worth more than "247°", so the
 * bearing is expressed relative to the way the golfer is facing when a heading
 * is available.
 */
export function relativeDirection(bearingToTarget: number, myHeading: number | null): string {
  if (myHeading == null) return compassPoint(bearingToTarget);
  const d = (((bearingToTarget - myHeading) % 360) + 360) % 360;
  if (d < 25 || d >= 335) return 'straight ahead';
  if (d < 70) return 'ahead right';
  if (d < 110) return 'to your right';
  if (d < 160) return 'behind right';
  if (d < 200) return 'behind you';
  if (d < 250) return 'behind left';
  if (d < 290) return 'to your left';
  return 'ahead left';
}
